import type { TSparseItem } from '@modules/items';

import { TAssessorFunction, TChangeCategory, TScopeType, TUrgency } from './change';
import { TTemplateCreationInputConfig } from './templateInputs';

// A template SUGGESTS request prefills, assessment sections, roles and tasks.
// Nothing a template proposes is ever applied silently: users review every
// suggestion, and regulatory conclusions are never pre-decided.

// The fields a template may prefill on the request. Kept intentionally narrow:
// a template never fills assessment answers, approvals or evidence.
export type TTemplateRequestPrefills = {
  title?: string;
  description?: string;
  reasonOrTrigger?: string;
  desiredOutcome?: string;
  implementationProposal?: string;
  scopeType?: TScopeType;
  urgency?: TUrgency;
  affectedQmsAreas?: string[];
  changeCategory?: TChangeCategory;
};

export type TSuggestedTask = {
  title: string;
  assignedFunction: TAssessorFunction | string;
  documents?: TSparseItem[];
};

export type TChangeTemplate = {
  id: string;
  // Bumped whenever the reusable template changes; snapshotted onto a created
  // Change so later template edits never mutate an existing record.
  version?: number;
  name: string;
  description: string;
  applicability: string;
  // Featured templates are surfaced first and highlighted in the creation gallery.
  featured?: boolean;
  // The few template-specific facts collected before a draft is generated. Their
  // values resolve `{{input:...}}` placeholders in the prefills below.
  creationInputs?: TTemplateCreationInputConfig[];
  requestPrefills: TTemplateRequestPrefills;
  assessmentSections: string[];
  suggestedRoles: TAssessorFunction[];
  suggestedTasks: TSuggestedTask[];
  suggestedAffectedObjectCategories: string[];
  suggestedWorkflowPolicy: string;
  // Opt in only when a template adds an unusual workflow or approval gate that
  // reviewers need to see without expanding the preview details.
  surfaceWorkflowPolicy?: boolean;
};

// The structure a previous Change can contribute. Assessment answers, approvals,
// signatures, evidence, completion dates and effectiveness conclusions are NEVER
// part of this - only reusable structure, role suggestions and task definitions.
export type TPreviousChange = {
  id: string;
  displayId: string;
  title: string;
  summary: string;
  scopeType: TScopeType;
  suggestedRoles: TAssessorFunction[];
  suggestedTasks: TSuggestedTask[];
  reusableStructure: string[];
};

// The information copied when reusing a previous Change - deliberately excludes
// everything that must be re-earned on the new record.
export const NEVER_COPIED_FROM_PREVIOUS: string[] = [
  'Assessment answers',
  'Approvals',
  'Signatures',
  'Evidence',
  'Completion dates',
  'Effectiveness conclusions',
];
